// Setup › KPI reporting (`/c/{slug}/setup/kpi-reporting?seat=`; AGENT-REPORTING, 13 §Reporting
// duties and 05 §Agent scopes, founder-approved 2026-10-21; managers, `kpi`). Pick a seat; for
// it: what its digital employee's runs may do with its KPIs (`seats.putKpiScopes`: read, record;
// the default is both), then one row per measured metric with its status (Next due / Late /
// Blocked / Reported, the last report, a value pending review), the metric's "Needs review" flag
// (`metrics.update needs_review`) and its reporting duty: method, due rule (frequency, weekday or
// day, time, the period it covers), reminder lead, evidence, who is reminded — with a live preview
// of the rule (domain-kpi `describeDueRule`) and, on a DE seat, the "Reporting" order the agent
// will read (`dutyOrderText`). Save is `reporting.putDuty`, Remove `reporting.deleteDuty`.
// One read for the whole screen: `GET seats/{id}/reporting`.
import {
  type DueRule,
  describeDueRule,
  dutyOrderText,
  type PeriodType,
  type ReportingMethod,
} from "@companyos/domain-kpi";
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsGroup,
  SettingsRow,
  Switch,
  ToggleRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GaugeIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { seatsQuery } from "../queries";
import { Loading } from "../screen-parts";
import { SetupPage } from "../settings/setup-page";
import { ConfirmButton, ErrorNotice, FormError, ReadOnlyNotice } from "../structure/parts";
import { useFeature } from "../use-feature";
import { ReportingStatusLine } from "./reporting-status";

type SeatReporting = NonNullable<Awaited<ReturnType<Api["client"]["reporting"]["seat"]>>>;
type MetricRow = SeatReporting["metrics"][number];
type Duty = NonNullable<MetricRow["duty"]>;
type Scope = "kpis:read" | "kpis:write";

export const reportingKey = (co: string, seat: string) =>
  ["company", co, "reporting", seat] as const;

export const seatReportingQuery = (api: Api, co: string, seat: string) => ({
  queryKey: reportingKey(co, seat),
  queryFn: () => api.client.reporting.seat({ params: { co, id: seat } }),
});

const METHOD_LABELS: Record<ReportingMethod, string> = {
  agent_tool: "The agent reports it (outcomes.record)",
  manual: "A person enters it",
  import: "CSV import",
  events: "Counted from events",
};
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PERIOD_OF: Record<string, PeriodType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};
const SOURCE_WORD: Record<MetricRow["source"], string> = {
  manual: "entered",
  import: "imported",
  event: "counted from events",
  formula: "derived",
  ledger: "summed from the cost ledger",
  task: "counted from a board's tasks",
};

export function KpiReportingScreen({
  api,
  company,
  seatId,
  onSeat,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string | undefined;
  onSeat: (id: string) => void;
}) {
  const kpi = useFeature("kpi");
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  const list = (seats.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (s) => s.status !== "closed",
  );
  const selected = seatId ?? list[0]?.id;
  const pickerId = useId();
  return (
    <SetupPage title="KPI reporting" icon={GaugeIcon} slot="kpi-reporting">
      <SettingsGroup>
        <SettingsRow
          label="Seat"
          hint="Who reports which number, and when. Each seat measures its own metrics."
          htmlFor={pickerId}
        >
          <NativeSelect
            id={pickerId}
            value={selected ?? ""}
            disabled={!list.length}
            onChange={(e) => onSeat(e.target.value)}
            className="min-w-64 max-md:w-full"
          >
            {list.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.title}
                {s.status === "open" ? " (open)" : ""}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </SettingsRow>
      </SettingsGroup>
      {seats.isPending ? (
        <Loading label="Loading seats" rows={2} />
      ) : seats.isError ? (
        <ErrorNotice what="Seats" error={seats.error} />
      ) : selected ? (
        <SeatReportingView
          key={selected}
          api={api}
          company={company}
          seatId={selected}
          readOnly={kpi.readOnly}
        />
      ) : (
        <EmptyState
          variant="card"
          icon={GaugeIcon}
          title="No seats yet"
          description="Add a seat and give it metrics in Setup › Seats; its reporting duties are set here."
        />
      )}
    </SetupPage>
  );
}

function SeatReportingView({
  api,
  company,
  seatId,
  readOnly,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  readOnly: boolean;
}) {
  const q = useQuery(seatReportingQuery(api, company.id, seatId));
  if (q.isPending) return <Loading label="Loading the seat's reporting" rows={4} />;
  if (q.isError) return <ErrorNotice what="The seat's reporting" error={q.error} />;
  const r = q.data;
  const locked = readOnly || !r.can_edit;
  const de = r.holder?.kind === "digital_employee";
  return (
    <>
      {readOnly ? <ReadOnlyNotice what="reporting duties" /> : null}
      <p className="text-sm text-muted-foreground" data-slot="seat-holder">
        {r.holder
          ? `Held by ${r.holder.name}${de ? " (digital employee)" : ""}.`
          : "Open seat: nobody holds it, so its duties are blocked."}{" "}
        Due times are in company time ({r.timezone}).
      </p>
      <ScopesGroup api={api} company={company} reporting={r} locked={locked} />
      <SettingsGroup
        title="Measured metrics"
        count={r.metrics.length}
        data-slot="reporting-metrics"
      >
        {r.metrics.length ? (
          // The metrics with a duty first, each group in the seat's order.
          [...r.metrics.filter((m) => m.duty), ...r.metrics.filter((m) => !m.duty)].map((m) => (
            <MetricReportingRow
              key={m.metric_def_id}
              api={api}
              company={company}
              reporting={r}
              metric={m}
              locked={locked}
            />
          ))
        ) : (
          <SettingsRow
            label="No measured metrics"
            hint="Give the seat metrics in Setup › Metrics & targets › Seat weights first."
          />
        )}
      </SettingsGroup>
    </>
  );
}

function ScopesGroup({
  api,
  company,
  reporting: r,
  locked,
}: {
  api: Api;
  company: CompanyAccess;
  reporting: SeatReporting;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const effective = r.kpi_scopes.effective as Scope[];
  const save = useMutation({
    mutationFn: (kpi_scopes: Scope[] | null) =>
      api.client.seats.putKpiScopes({
        params: { co: company.id, id: r.seat.id },
        body: { kpi_scopes },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["company", company.id] }),
  });
  const toggle = (scope: Scope, on: boolean) => {
    const next = (["kpis:read", "kpis:write"] as Scope[]).filter((s) =>
      s === scope ? on : effective.includes(s),
    );
    save.mutate(next);
  };
  const isDefault = r.kpi_scopes.kpi_scopes === null;
  return (
    <SettingsGroup
      title="Agent access to KPIs"
      data-slot="kpi-scopes"
      actions={
        !isDefault && !locked ? (
          <Button size="sm" variant="ghost" onClick={() => save.mutate(null)}>
            Use the default
          </Button>
        ) : (
          <Badge variant="neutral">{isDefault ? "Default" : "Custom"}</Badge>
        )
      }
    >
      <div className="px-4 py-3">
        <p className="mb-3 text-xs text-muted-foreground">
          What a digital employee's runs on this seat may do with its KPIs. By default it may do
          both; a person-held seat is not affected.
          {r.holder?.kind === "digital_employee" ? "" : " Nobody's agent runs on this seat now."}
        </p>
        <ToggleRow
          label="Agent may read KPIs"
          description="The run's “Your KPIs” block and kpis.read: targets, the last values and its reporting orders."
          checked={effective.includes("kpis:read")}
          disabled={locked || save.isPending}
          onCheckedChange={(v) => toggle("kpis:read", v)}
        />
        <ToggleRow
          label="Agent may record KPIs"
          description="outcomes.record. Off: agent-tool duties on this seat are blocked."
          checked={effective.includes("kpis:write")}
          disabled={locked || save.isPending}
          onCheckedChange={(v) => toggle("kpis:write", v)}
        />
        <FormError error={save.error} what="Saving the KPI access" />
      </div>
    </SettingsGroup>
  );
}

function MetricReportingRow({
  api,
  company,
  reporting: r,
  metric: m,
  locked,
}: {
  api: Api;
  company: CompanyAccess;
  reporting: SeatReporting;
  metric: MetricRow;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["company", company.id] });
  const review = useMutation({
    mutationFn: (needs_review: boolean) =>
      api.client.metrics.update({
        params: { co: company.id, id: m.metric_def_id },
        body: { needs_review },
      }),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: () =>
      api.client.reporting.deleteDuty({
        params: { co: company.id, id: r.seat.id, metric_def_id: m.metric_def_id },
      }),
    onSettled: refresh,
  });
  const reviewId = useId();
  return (
    <div
      className="flex flex-col gap-3 border-t border-subtle px-4 py-3 first:border-t-0"
      data-slot="reporting-metric"
      data-metric={m.key}
    >
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[13px] font-medium text-foreground">{m.name}</span>
            <span className="font-mono text-2xs text-muted-foreground">{m.key}</span>
            <Badge variant="outline">
              {m.cadence ?? "no cadence"} · {SOURCE_WORD[m.source]}
            </Badge>
            {m.needs_review ? <Badge variant="info">Needs review</Badge> : null}
          </span>
          {m.duty ? (
            <ReportingStatusLine
              reporting={m.status}
              lastReport={m.last_report}
              pendingReview={m.pending_review}
            />
          ) : (
            <span className="text-xs text-muted-foreground">
              No reporting duty: nobody is reminded and it is never late.
              {m.last_report
                ? ` Last value ${m.last_report.value} for ${m.last_report.period_key}.`
                : ""}
            </span>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <label
            htmlFor={reviewId}
            className="flex items-center gap-2 text-xs text-muted-foreground"
            title="An agent's value waits for the seat's supervisor in the approvals queue before it counts."
          >
            Needs review
            <Switch
              id={reviewId}
              checked={m.needs_review}
              disabled={locked || review.isPending || m.report === "none"}
              onCheckedChange={(v) => review.mutate(v)}
            />
          </label>
          {locked ? null : m.duty ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => setEditing((v) => !v)}>
                <PencilIcon data-icon="inline-start" />
                {editing ? "Close" : "Edit duty"}
              </Button>
              <ConfirmButton
                label={
                  <>
                    <Trash2Icon data-icon="inline-start" />
                    Remove
                  </>
                }
                srLabel={`Remove the reporting duty of ${m.name}`}
                title={`Remove the reporting duty of ${m.name}?`}
                description="Nobody is reminded any more and the metric is never late. Its numbers stay."
                confirmLabel="Remove duty"
                pending={remove.isPending}
                onConfirm={() => remove.mutateAsync()}
              />
            </>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setEditing((v) => !v)}>
              <PlusIcon data-icon="inline-start" />
              {editing ? "Close" : "Add duty"}
            </Button>
          )}
        </div>
      </div>
      <FormError error={review.error} what="Saving the review flag" />
      <FormError error={remove.error} what="Removing the duty" />
      {editing && !locked ? (
        <DutyEditor
          api={api}
          company={company}
          reporting={r}
          metric={m}
          onDone={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
}

type Form = {
  method: ReportingMethod;
  freq: DueRule["freq"];
  weekday: number;
  day: number;
  time: string;
  covers: "current" | "previous";
  lead: string;
  evidence: boolean;
  holder: boolean;
  supervisor: boolean;
};

function formOf(duty: Duty | null, m: MetricRow, de: boolean): Form {
  const rule = (duty?.due_rule ?? null) as DueRule | null;
  return {
    method:
      duty?.method ??
      (m.report === "event" ? "events" : de && m.report === "value" ? "agent_tool" : "manual"),
    freq:
      rule?.freq ??
      (m.cadence === "monthly" ? "monthly" : m.cadence === "daily" ? "daily" : "weekly"),
    weekday: rule?.freq === "weekly" ? rule.weekday : 5,
    day: rule?.freq === "monthly" ? rule.day : 1,
    time: rule?.time ?? "17:00",
    covers: rule?.covers ?? "current",
    lead: String(duty?.lead_minutes ?? 60),
    evidence: duty?.evidence_required ?? de,
    holder: duty ? duty.remind.includes("holder") : true,
    supervisor: duty ? duty.remind.includes("supervisor") : false,
  };
}

function ruleOf(f: Form): DueRule {
  const covers = f.covers;
  if (f.freq === "daily") return { freq: "daily", time: f.time, covers };
  if (f.freq === "weekly") return { freq: "weekly", weekday: f.weekday, time: f.time, covers };
  return { freq: "monthly", day: f.day, time: f.time, covers };
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function DutyEditor({
  api,
  company,
  reporting: r,
  metric: m,
  onDone,
}: {
  api: Api;
  company: CompanyAccess;
  reporting: SeatReporting;
  metric: MetricRow;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const de = r.holder?.kind === "digital_employee";
  const [f, setF] = useState<Form>(() => formOf(m.duty, m, de));
  const set = (patch: Partial<Form>) => setF((x) => ({ ...x, ...patch }));
  const ids = {
    method: useId(),
    freq: useId(),
    when: useId(),
    time: useId(),
    covers: useId(),
    lead: useId(),
  };
  const lead = Number(f.lead);
  const leadOk = Number.isInteger(lead) && lead >= 0 && lead <= 1440;
  const dayOk = f.freq !== "monthly" || (Number.isInteger(f.day) && f.day >= 1 && f.day <= 31);
  const ok = TIME.test(f.time) && leadOk && dayOk;
  const period = m.cadence ? (PERIOD_OF[m.cadence] ?? null) : null;
  const rule = ruleOf(f);
  const label = TIME.test(f.time) ? describeDueRule(rule, period ?? undefined) : "—";
  const order =
    de && period && TIME.test(f.time)
      ? dutyOrderText({
          metricName: m.name,
          metricKey: m.key,
          method: f.method,
          rule,
          timezone: r.timezone,
          periodType: period,
          report: m.report,
          evidenceRequired: f.evidence,
        })
      : null;
  const save = useMutation({
    mutationFn: () =>
      api.client.reporting.putDuty({
        params: { co: company.id, id: r.seat.id, metric_def_id: m.metric_def_id },
        body: {
          method: f.method,
          due_rule: rule,
          lead_minutes: lead,
          evidence_required: f.evidence,
          remind: [
            ...(f.holder ? (["holder"] as const) : []),
            ...(f.supervisor ? (["supervisor"] as const) : []),
          ],
        },
      }),
    onSuccess: () => onDone(),
    onSettled: () => qc.invalidateQueries({ queryKey: ["company", company.id] }),
  });
  const word = period ?? "period";
  return (
    <section
      aria-label={`Reporting duty of ${m.name}`}
      className="flex flex-col gap-4 rounded-md border border-border bg-sunken p-4"
      data-slot="duty-editor"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field>
          <FieldLabel htmlFor={ids.method}>Method</FieldLabel>
          <NativeSelect
            id={ids.method}
            value={f.method}
            onChange={(e) => set({ method: e.target.value as ReportingMethod })}
          >
            {(Object.keys(METHOD_LABELS) as ReportingMethod[]).map((k) => (
              <NativeSelectOption key={k} value={k}>
                {METHOD_LABELS[k]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.freq}>Due</FieldLabel>
          <NativeSelect
            id={ids.freq}
            value={f.freq}
            onChange={(e) => set({ freq: e.target.value as Form["freq"] })}
          >
            <NativeSelectOption value="daily">Every day</NativeSelectOption>
            <NativeSelectOption value="weekly">Every week</NativeSelectOption>
            <NativeSelectOption value="monthly">Every month</NativeSelectOption>
          </NativeSelect>
        </Field>
        {f.freq === "weekly" ? (
          <Field>
            <FieldLabel htmlFor={ids.when}>Weekday</FieldLabel>
            <NativeSelect
              id={ids.when}
              value={String(f.weekday)}
              onChange={(e) => set({ weekday: Number(e.target.value) })}
            >
              {WEEKDAYS.map((d, i) => (
                <NativeSelectOption key={d} value={String(i + 1)}>
                  {d}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : f.freq === "monthly" ? (
          <Field>
            <FieldLabel htmlFor={ids.when}>Day of the month</FieldLabel>
            <Input
              id={ids.when}
              type="number"
              min={1}
              max={31}
              value={String(f.day)}
              onChange={(e) => set({ day: Number(e.target.value) })}
              aria-invalid={!dayOk || undefined}
            />
            <FieldDescription>Past the month's end it is the last day.</FieldDescription>
          </Field>
        ) : null}
        <Field>
          <FieldLabel htmlFor={ids.time}>Time ({r.timezone})</FieldLabel>
          <Input
            id={ids.time}
            type="time"
            value={f.time}
            onChange={(e) => set({ time: e.target.value })}
            aria-invalid={!TIME.test(f.time) || undefined}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.covers}>Reports</FieldLabel>
          <NativeSelect
            id={ids.covers}
            value={f.covers}
            onChange={(e) => set({ covers: e.target.value as Form["covers"] })}
          >
            <NativeSelectOption value="current">The {word} it falls in</NativeSelectOption>
            <NativeSelectOption value="previous">The previous {word}</NativeSelectOption>
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.lead}>Remind before (minutes)</FieldLabel>
          <Input
            id={ids.lead}
            type="number"
            min={0}
            max={1440}
            value={f.lead}
            onChange={(e) => set({ lead: e.target.value })}
            aria-invalid={!leadOk || undefined}
          />
          <FieldDescription>0 to 1440; a digital employee is woken then.</FieldDescription>
        </Field>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <CheckField
          label="Evidence required"
          checked={f.evidence}
          onChange={(v) => set({ evidence: v })}
        />
        <CheckField
          label={`Remind the holder${de ? " (wakes the agent)" : ""}`}
          checked={f.holder}
          onChange={(v) => set({ holder: v })}
        />
        <CheckField
          label="Remind the supervisor"
          checked={f.supervisor}
          onChange={(v) => set({ supervisor: v })}
        />
      </div>
      <Notice tone="info" title={`Due: ${label}`} data-slot="duty-preview">
        {order ? (
          <span className="flex flex-col gap-1">
            <span className="text-xs font-medium">Reporting order the agent will read:</span>
            <span className="text-sm break-words" data-slot="duty-order">
              {order}
            </span>
          </span>
        ) : de ? (
          "The metric has no cadence: nothing is ever due."
        ) : (
          `${r.holder?.name ?? "The holder"} is reminded in the inbox${f.supervisor ? ", and so is the seat's supervisor" : ""}.`
        )}
      </Notice>
      <FormError error={save.error} what="Saving the duty" />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!ok || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save duty"}
        </Button>
      </div>
    </section>
  );
}

function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <span className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <label htmlFor={id}>{label}</label>
    </span>
  );
}
